import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { type MenuItem } from '@/types/menu'

interface SidebarMenuItemProps {
	item: MenuItem
	collapsed: boolean
	level?: number
}

export function SidebarMenuItem({ item, collapsed, level = 0 }: SidebarMenuItemProps) {
	const location = useLocation()
	const [isOpen, setIsOpen] = useState(false)
	const hasChildren = item.children && item.children.length > 0

	// 检查路径是否匹配（支持动态路由，如 /users/departments/2 匹配 /users/departments）
	const isPathMatch = (href: string | undefined) => {
		if (!href) return false
		// 精确匹配或前缀匹配（确保前缀是完整路径段）
		return location.pathname === href ||
			location.pathname.startsWith(href + '/') ||
			(location.pathname + '/').startsWith(href + '/')
	}

	const isActive = isPathMatch(item.href)
	const activeChildHref = item.children
		?.filter(child => isPathMatch(child.href))
		.sort((a, b) => (b.href?.length ?? 0) - (a.href?.length ?? 0))[0]?.href
	const isChildActive = Boolean(activeChildHref)

	// 初始化时如果有子菜单被激活，则展开父菜单
	useEffect(() => {
		if (isChildActive) {
			setIsOpen(true)
		}
	}, [isChildActive])

	const Icon = item.icon

	// 折叠状态下只显示图标
	if (collapsed) {
		return (
			<Tooltip delayDuration={0}>
				<TooltipTrigger asChild>
					{hasChildren ? (
						<div
							className={cn(
								"flex h-9 w-9 items-center justify-center rounded-lg transition-colors relative",
								"hover:bg-muted cursor-pointer",
								(isActive || isChildActive) && "bg-muted text-accent"
							)}
							onClick={() => setIsOpen(!isOpen)}
						>
							{Icon && <Icon className="h-5 w-5" />}
							{item.badge && (
								<span className="absolute right-0 top-0 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground">
									{item.badge}
								</span>
							)}
						</div>
					) : (
						<Link to={item.href || '#'}>
							<div
								className={cn(
									"flex h-9 w-9 items-center justify-center rounded-lg transition-colors relative",
									"hover:bg-muted",
									isActive && "bg-muted text-accent"
								)}
							>
								{Icon && <Icon className="h-5 w-5" />}
								{item.badge && (
									<span className="absolute right-0 top-0 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground">
										{item.badge}
									</span>
								)}
							</div>
						</Link>
					)}
				</TooltipTrigger>
				<TooltipContent side="right" className="flex items-center gap-2">
					{item.label}
					{item.badge && (
						<span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-destructive px-1.5 text-xs font-medium text-destructive-foreground">
							{item.badge}
						</span>
					)}
				</TooltipContent>
			</Tooltip>
		)
	}

	// 有子菜单的情况
	if (hasChildren) {
		return (
			<Collapsible open={isOpen} onOpenChange={setIsOpen}>
				<CollapsibleTrigger asChild>
					<Button
						variant="ghost"
						className={cn(
							"w-full justify-start gap-2 px-3 h-9 font-normal",
							level > 0 && "pl-6",
							isActive && "bg-muted"
						)}
					>
						{Icon && <Icon className="h-5 w-5 shrink-0" />}
						<span className="flex-1 text-left truncate">{item.label}</span>
						{item.badge && (
							<span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-destructive px-1.5 text-xs font-medium text-destructive-foreground">
								{item.badge}
							</span>
						)}
						<ChevronDown
							className={cn(
								"h-4 w-4 shrink-0 transition-transform duration-200",
								isOpen && "rotate-180"
							)}
						/>
					</Button>
				</CollapsibleTrigger>
				<CollapsibleContent className="space-y-1 pt-1">
					{item.children!.map((child) => (
						<SidebarMenuItemItem
							key={child.id}
							item={child}
							level={level + 1}
							isActive={child.href === activeChildHref}
						/>
					))}
				</CollapsibleContent>
			</Collapsible>
		)
	}

	// 无子菜单的情况
	return (
		<Link to={item.href || '#'}>
			<Button
				variant="ghost"
				className={cn(
					"w-full justify-start gap-2 px-3 h-9 font-normal",
					level > 0 && "pl-6",
					isActive && "bg-muted text-accent"
				)}
			>
				{Icon && <Icon className="h-5 w-5 shrink-0" />}
				<span className="flex-1 text-left truncate">{item.label}</span>
				{item.badge && (
					<span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-destructive px-1.5 text-xs font-medium text-destructive-foreground">
						{item.badge}
					</span>
				)}
			</Button>
		</Link>
	)
}

// 子菜单项组件
interface SidebarMenuItemItemProps {
	item: MenuItem
	level: number
	isActive: boolean
}

function SidebarMenuItemItem({ item, level, isActive }: SidebarMenuItemItemProps) {
	const Icon = item.icon

	return (
		<Link to={item.href || '#'}>
			<Button
				variant="ghost"
				className={cn(
					"w-full justify-start gap-2 px-3 h-8 font-normal text-sm",
					level > 0 && "pl-9",
					isActive && "bg-muted text-accent"
				)}
			>
				{Icon && <Icon className="h-4 w-4 shrink-0" />}
				<span className="flex-1 text-left truncate">{item.label}</span>
				{item.badge && (
					<span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground">
						{item.badge}
					</span>
				)}
			</Button>
		</Link>
	)
}
